/**
 * Cold-start offline selling (README.md Phase 21) — a sale that starts with
 * zero connectivity, from its very first line through payment, entirely out
 * of the IndexedDB `offline-bills` store and the catalog cache
 * (offline-catalog.ts). Complements offline-queue.ts, which only ever
 * handled adding a line to a bill that *already* had a server id.
 *
 * Deliberately does NOT support bill-level discounts, loyalty redemption,
 * store credit, gift vouchers, or a batch/serial-tracked line — each needs
 * either a live balance check or a live FEFO pick the browser cannot safely
 * fake offline. An offline sale is plain lines at the cached counter price,
 * paid by cash/card/wallet/credit with a reference. Every screen still
 * offers everything else; those actions simply require being online, same
 * as starting a sale used to before this file existed.
 *
 * Every total shown while a bill is still offline is computed here with
 * `calcBill` — the same pure function the server uses — but WITHOUT the
 * shop's real `sales.roundingStepPaisa`/discount-basis settings, which
 * aren't part of the cached snapshot. Once replayed, the server recomputes
 * for real and the synced bill (which can differ by a few paisa of rounding)
 * replaces this estimate — the same "corrected on sync" contract
 * offline-queue.ts's optimistic rows already have.
 */
import { calcBill, extendLine, type LineInput } from '@pospak/shared';
import { api } from './api';
import { uuid } from './uuid';
import { isNetworkFailure } from './offline-queue';
import {
  getCatalogCache,
  getOfflineBillRecord,
  listOfflineBillRecords,
  putOfflineBill,
  removeOfflineBill,
  type OfflineBillLine,
  type OfflineBillPayment,
  type OfflineBillRecord,
  type OfflineCatalogItem,
} from './offline-db';
import { takeNextBillNo } from './offline-block';
import type { Bill, BillLine } from '../features/selling';

export const OFFLINE_BILL_PREFIX = 'local:';

export function isOfflineBillId(billId: string | null): billId is string {
  return !!billId && billId.startsWith(OFFLINE_BILL_PREFIX);
}

export function toLocalId(billId: string): string {
  return billId.slice(OFFLINE_BILL_PREFIX.length);
}

/**
 * Starts a brand-new offline sale with its first line. Returns `null` (never
 * throws) if this terminal has nothing to work with — no cached catalog for
 * this shop, or no reserved bill number left — so the caller can fall back
 * to the existing "you're offline, try again once connected" message rather
 * than starting a sale it can't give a real number to.
 */
export async function startOfflineBill(shopId: string, item: OfflineCatalogItem, qtyMilli: number): Promise<Bill | null> {
  const cache = await getCatalogCache(shopId);
  if (!cache) return null;
  const billNo = await takeNextBillNo(shopId);
  if (!billNo) return null;

  const record: OfflineBillRecord = {
    localId: uuid(),
    shopId,
    billNo,
    status: 'OPEN',
    createdAt: Date.now(),
    customerName: null,
    customerPhone: null,
    lines: [lineFromItem(item, qtyMilli)],
    payments: [],
  };
  await putOfflineBill(record);
  return toDisplayBill(record, cache);
}

function lineFromItem(item: OfflineCatalogItem, qtyMilli: number): OfflineBillLine {
  return {
    lineId: uuid(),
    variantId: item.variantId,
    productName: item.productName,
    variantName: item.variantName,
    unit: item.unit,
    qtyMilli,
    unitPricePaisa: item.pricePaisa,
    priceMode: item.priceMode,
    taxRateBps: item.taxRateBps,
    taxExempt: item.taxExempt,
  };
}

export async function addOfflineLine(localId: string, item: OfflineCatalogItem, qtyMilli: number): Promise<Bill> {
  const record = await getOfflineBillRecord(localId);
  if (!record) throw new Error('Offline bill not found');

  // Same "rescan bumps quantity" merge the server's addLineInTx does.
  const mergeable = record.lines.find(
    (l) => l.variantId === item.variantId && l.unitPricePaisa === item.pricePaisa && l.priceMode === item.priceMode,
  );
  if (mergeable) {
    mergeable.qtyMilli += qtyMilli;
  } else {
    record.lines.push(lineFromItem(item, qtyMilli));
  }
  await putOfflineBill(record);
  return toDisplayBillFromShop(record);
}

export async function updateOfflineLineQty(localId: string, lineId: string, qtyMilli: number): Promise<Bill> {
  const record = await getOfflineBillRecord(localId);
  if (!record) throw new Error('Offline bill not found');
  const line = record.lines.find((l) => l.lineId === lineId);
  if (line) line.qtyMilli = qtyMilli;
  await putOfflineBill(record);
  return toDisplayBillFromShop(record);
}

export async function removeOfflineLine(localId: string, lineId: string): Promise<Bill> {
  const record = await getOfflineBillRecord(localId);
  if (!record) throw new Error('Offline bill not found');
  record.lines = record.lines.filter((l) => l.lineId !== lineId);
  await putOfflineBill(record);
  return toDisplayBillFromShop(record);
}

export async function payOfflineBill(localId: string, payments: OfflineBillPayment[]): Promise<Bill> {
  const record = await getOfflineBillRecord(localId);
  if (!record) throw new Error('Offline bill not found');
  record.payments = payments;
  record.status = 'PAID';
  record.paidAt = Date.now();
  await putOfflineBill(record);
  return toDisplayBillFromShop(record);
}

export async function getOfflineBill(localId: string): Promise<Bill | null> {
  const record = await getOfflineBillRecord(localId);
  if (!record) return null;
  return toDisplayBillFromShop(record);
}

export async function listOpenOfflineBills(): Promise<OfflineBillRecord[]> {
  const all = await listOfflineBillRecords();
  return all.filter((r) => r.status === 'OPEN' || r.status === 'PAID');
}

export async function listFailedOfflineBills(): Promise<OfflineBillRecord[]> {
  const all = await listOfflineBillRecords();
  return all.filter((r) => r.status === 'FAILED');
}

export async function discardOfflineBill(localId: string): Promise<void> {
  await removeOfflineBill(localId);
}

/**
 * Replays every OPEN/PAID offline bill to the server, oldest first, stopping
 * the moment a genuine network failure shows up again (so the rest retry
 * next time, in the same order).
 *
 * Deliberately does NOT send the offline-cached price/tax on the create
 * step — that would need bypassing `SALES_PRICE_OVERRIDE`, and a price that
 * quietly changed between the offline sale and the replay is exactly the
 * kind of conflict README.md Phase 21 says must surface, not be trusted
 * through silently. The server re-resolves each line at today's price
 * instead. `reservedBillNo` still rides along, so the bill number this
 * terminal already printed on the receipt is the one the server actually
 * uses (`bill.service.ts#createDraft`'s `if (!billNo)` guard skips minting
 * a second one) — only the price can drift, never the number.
 *
 * If the resolved price is now higher than what was actually collected
 * offline (or a line's stock went negative since), `pay()` throws
 * (`UNDERPAID`/`INSUFFICIENT_STOCK`/etc.) — the bill still exists server-side
 * as a normal DRAFT/UNPAID bill by that point, so nothing is lost; this just
 * marks the local record FAILED with `serverBillId` set, and the
 * conflict-review screen (features/offline-sync) links straight to it. Staff
 * resolve it exactly like any other unfinished bill — no bespoke merge UI.
 */
export async function replayOfflineBills(): Promise<{ syncedAny: boolean }> {
  const records = await listOpenOfflineBills();
  let syncedAny = false;

  for (const record of records) {
    let created: Bill | undefined;
    try {
      created = record.serverBillId
        ? await api.get<Bill>(`/bills/${record.serverBillId}`)
        : await api.post<Bill>('/bills', {
            shopId: record.shopId,
            clientOpId: record.localId,
            reservedBillNo: record.billNo,
            lines: record.lines.map((l) => ({ variantId: l.variantId, qtyMilli: l.qtyMilli })),
          });

      if (record.status === 'PAID' && created.status !== 'PAID') {
        await api.post<Bill>(`/bills/${created.id}/pay`, {
          clientOpId: record.localId,
          payments: record.payments,
        });
      }

      await removeOfflineBill(record.localId);
      syncedAny = true;
    } catch (err) {
      if (isNetworkFailure(err)) break; // still offline — stop, retry the rest next time
      await putOfflineBill({
        ...record,
        status: 'FAILED',
        // `created` is set whenever the create step landed and only the
        // *pay* step (loop below) is what failed — a real server bill to
        // link to, sitting there as a normal DRAFT/UNPAID bill.
        serverBillId: created?.id ?? record.serverBillId,
        lastError: err instanceof Error ? err.message : 'Could not sync this sale',
      });
    }
  }

  return { syncedAny };
}

async function toDisplayBillFromShop(record: OfflineBillRecord): Promise<Bill> {
  const cache = await getCatalogCache(record.shopId);
  return toDisplayBill(record, cache ? { shop: cache.shop, business: cache.business } : null);
}

function toDisplayBill(
  record: OfflineBillRecord,
  header: { shop: { code: string; name: string; phone: string | null; address: string | null }; business: { name: string; phone: string | null; address: string | null; ntn: string | null; strn: string | null } } | null,
): Bill {
  const calcInput: LineInput[] = record.lines.map((l) => ({
    unitPricePaisa: l.unitPricePaisa,
    priceMode: l.priceMode as LineInput['priceMode'],
    qtyMilli: l.qtyMilli,
    taxRateBps: l.taxRateBps,
    taxExempt: l.taxExempt,
  }));
  const calc = calcBill({ lines: calcInput });

  const lines: BillLine[] = record.lines.map((l, i) => {
    const r = calc.lines[i];
    return {
      id: l.lineId,
      lineNo: i + 1,
      variantId: l.variantId,
      productName: l.productName,
      variantName: l.variantName,
      sku: null,
      unit: l.unit,
      qtyMilli: l.qtyMilli,
      unitPricePaisa: l.unitPricePaisa,
      priceMode: l.priceMode,
      discountPaisa: 0,
      apportionedDiscountPaisa: 0,
      taxRateBps: l.taxRateBps,
      taxExempt: l.taxExempt,
      netPaisa: r?.netPaisa ?? extendLine(l.unitPricePaisa, l.qtyMilli),
      taxPaisa: r?.taxPaisa ?? 0,
      grossPaisa: r?.grossPaisa ?? extendLine(l.unitPricePaisa, l.qtyMilli),
      refundedQtyMilli: 0,
    };
  });

  const paidPaisa = record.payments.reduce((a, p) => a + p.amountPaisa, 0);
  const cashTendered = record.payments
    .filter((p) => p.method === 'CASH')
    .reduce((a, p) => a + (p.tenderedPaisa ?? p.amountPaisa), 0);
  const cashPortion = record.payments.filter((p) => p.method === 'CASH').reduce((a, p) => a + p.amountPaisa, 0);
  const changePaisa = Math.max(0, cashTendered - cashPortion);

  return {
    id: `${OFFLINE_BILL_PREFIX}${record.localId}`,
    billNo: record.billNo,
    status: record.status === 'PAID' ? 'PAID' : 'DRAFT',
    shopId: record.shopId,
    shop: header?.shop,
    business: header?.business,
    createdAt: new Date(record.createdAt).toISOString(),
    customerId: null,
    customerName: record.customerName,
    customerPhone: record.customerPhone,
    subtotalNetPaisa: calc.subtotalNetPaisa,
    lineDiscountPaisa: calc.lineDiscountPaisa,
    billDiscountPaisa: calc.billDiscountPaisa,
    loyaltyDiscountPaisa: calc.loyaltyDiscountPaisa,
    taxableNetPaisa: calc.taxableNetPaisa,
    taxPaisa: calc.taxPaisa,
    roundingPaisa: calc.roundingPaisa,
    totalPaisa: calc.totalPaisa,
    paidPaisa,
    changePaisa,
    refundedPaisa: 0,
    lines,
    payments: record.payments.map((p, i) => ({ id: `offline:${i}`, method: p.method, amountPaisa: p.amountPaisa, reference: p.reference ?? null })),
    discounts: [],
  };
}
